import { useMutation } from '@tanstack/react-query';
import styled from 'styled-components';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { CardContent, CardSubSection } from './styles';
import Button from 'design-system/src/components/Button/Button';
import { Card } from 'design-system/src/components/Card/Card';
import { client } from '../../api.tsx';
import { useState, useEffect, useRef } from 'react';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { toast } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { SignupError } from './SignupError.tsx';
import emailImage from 'design-system/src/assets/icons/gradientEmail.png';
import { PageContainerWithLogo } from './PageContainerWithLogo.tsx';
import { handleServerError } from '../../utils';

const ResendLink = styled(Button)`
  color: var(--purple-700);
`;

const ResendContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const EmailImage = styled.img`
  height: 64px;
  width: 64px;
  object-fit: contain;
`;

const ImagePanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

export type VerificationEmailProps = {
  className?: string;
  email: string | null;
};

type SetUpUserAndPracticeResponse = {
  data: {
    organization_id: string;
  };
};

/**
 * This is where we call the backend route to set up the user and practice
 * Users reach this page during signup or after booking, skipping, or returning from a demo
 */
export const VerificationEmail = ({ className, email }: VerificationEmailProps) => {
  const [resentClicked, setResentClicked] = useState(false);
  const hasInitialEmailSent = useRef(false);
  const signupId = localStorage.getItem('signupId');
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const {
    mutate: submitSetUpUserAndPractice,
    isPending: isSubmittingSetUpUserAndPractice,
    isError: isErrorSetUpUserAndPractice,
  } = useMutation({
    mutationFn: async (signupId: string): Promise<SetUpUserAndPracticeResponse> => {
      return client.post('/signup/set-up-user-and-practice-if-not-exists', {
        signup_id: signupId,
      });
    },
    onSuccess: (response: SetUpUserAndPracticeResponse) => {
      const data = response.data;
      setOrganizationId(data.organization_id);
      sendEmailVerification(data.organization_id);
    },
    onError: (error) => {
      console.error('Error setting up user and practice');
      const errorMessage = handleServerError(error);
      console.error(errorMessage);
    },
  });

  const {
    mutate: sendEmailVerification,
    isPending: isSendingEmailVerification,
    isError: isErrorSendingEmailVerification,
  } = useMutation({
    mutationFn: async (organization_id: string) => {
      if (!email) {
        Sentry.captureException('[VerificationEmail] Missing email in form state');
        console.error('[VerificationEmail] Missing email in form state');
        setIsError(true);
        return;
      }
      return client.post('/signup/send-verification-email', {
        organization_id: organization_id,
        email: email,
      });
    },
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Verification email sent</Toast>);
    },
    onError: (error) => {
      console.error('Error sending verification email');
      const errorMessage = handleServerError(error);
      toast.custom(() => <Toast variant="error">{errorMessage}</Toast>);
    },
  });

  // Set up account and send verification email on first component mount
  useEffect(() => {
    if (!signupId) {
      Sentry.captureException('[VerificationEmail] Missing signup ID in local storage');
      console.error('[VerificationEmail] Missing signup ID in local storage');
      setIsError(true);
      return;
    }
    if (!hasInitialEmailSent.current) {
      submitSetUpUserAndPractice(signupId!);
      hasInitialEmailSent.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = () => {
    if (!isSendingEmailVerification) {
      sendEmailVerification(organizationId!);
      setResentClicked(true);
    }
  };

  if (isError || isErrorSetUpUserAndPractice || isErrorSendingEmailVerification) {
    return <SignupError />;
  }

  return (
    <PageContainerWithLogo className={className} data-fs-unmask="true">
      <Card>
        <CardContent>
          <ImagePanel>
            <EmailImage src={emailImage} alt="Email" />
          </ImagePanel>
          <CardSubSection>
            <Typography styledAs="h6">Verify your email</Typography>
            <Typography styledAs="bodyDMSans">
              We sent a verification link to {email}. Check your inbox (and spam folder) to continue.
            </Typography>
          </CardSubSection>
          {!resentClicked && (
            <ResendContainer>
              <Typography styledAs="bodySmallDMSans" weight={600} color="var(--purple-900)" renderedAs="div">
                Haven't received an email?{' '}
                <ResendLink
                  variant="link"
                  onClick={handleResend}
                  disabled={isSendingEmailVerification || isSubmittingSetUpUserAndPractice}
                >
                  Resend now
                </ResendLink>
              </Typography>
            </ResendContainer>
          )}
        </CardContent>
      </Card>
    </PageContainerWithLogo>
  );
};

export default VerificationEmail;
